/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Dimensions, ActivityIndicator } from 'react-native';
import { useDispatch } from 'react-redux';
import { BgWrapper, FormWrapper, FormGroup } from '@components/screen/Layout';
import * as Auth from '@/store/auth/actions';
import Toast from 'react-native-toast-message';
import { isWeb, isNative, isMobileWeb } from '@global/screens';
import { logEvent, logEventScreen } from '@global/analytics';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import ModalFull from '@components/modal/ModalFull';
import Image from '@components/global/Image';
import Input from '@components/form/Input';
import InputPassword from '@components/form/InputPassword';
import VersionApp from '@components/global/VersionApp';

import crashlytics from '@/plugins/crashlytics';
import { IS_SIMULATOR, PASSWORD_REGEX } from '@constants';

const screenHeight = Dimensions.get('window').height;
const Logo = React.memo(function Logo() {
  return (
    <View style={styles.itemCenter}>
      <Image
        style={{ width: 200, height: 200 }}
        width={200}
        height={200}
        resizeMode="contain"
        source={require('@assets/img/logo/nina_2025.svg')}
        svg={true}
      />
    </View>
  );
});

const renderButton = (testID, onPress, disabled, title) => (
  <View style={styles.loginButton}>
    <Button
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      title={
        <SociollaText type="bold" style={styles.titleText}>
          {title}
        </SociollaText>
      }
    />
  </View>
);

const ModalForgotPassword = ({
  isForgetPasswordModal,
  setIsForgetPasswordModal,
}) => {
  const dispatch = useDispatch();
  const [isSuccess, setIsSuccess] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState();
  const [isLoadingRecoveryEmail, setIsLoadingRecoveryEmail] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const successForgetPass = () => {
    setIsForgetPasswordModal(false);
    setIsSuccess(false);
  };

  const handleSubmit = async () => {
    setIsLoadingRecoveryEmail(true);
    try {
      const trimmedEmail = recoveryEmail.trim();
      const trimmedRecoveryEmail = { email: trimmedEmail };
      const validateEmail = global.validate(
        {
          email: recoveryEmail,
        },
        {
          email: {
            presence: { allowEmpty: false },
          },
        },
      );
      if (validateEmail?.email) {
        setErrorMsg(validateEmail?.email?.[0]);
        setIsLoadingRecoveryEmail(false);
        return;
      }

      const response = await dispatch(
        Auth.forgetPassword(trimmedRecoveryEmail),
      );
      if (response) {
        setRecoveryEmail('');
        setIsSuccess(true);
      } else {
        setErrorMsg('Email not registered');
      }
    } catch (error) {
      console.log('err', error);
    } finally {
      setIsLoadingRecoveryEmail(false);
    }
  };

  return (
    <ModalFull
      testID="forgot-password"
      title="Forgot Password"
      show={isForgetPasswordModal}
      onClose={() => {
        setIsForgetPasswordModal(false);
        setRecoveryEmail();
        setErrorMsg('');
      }}>
      <View style={styles.loginWrapper}>
        <Logo />
        {!isSuccess ? (
          <>
            <SociollaText style={styles.descForgetPassword}>
              Please enter your registered email address to reset your password.
            </SociollaText>
            <FormWrapper>
              <FormGroup>
                <View style={styles.marginBottom}>
                  <Input
                    testID="forgot-password-email-input"
                    label={'Email Address'}
                    placeholder=""
                    onChangeText={(text) => {
                      if (errorMsg) {
                        setErrorMsg('');
                      }
                      setRecoveryEmail(text.replace(/\s/g, ''));
                    }}
                    onFocus={() => setErrorMsg('')}
                    hasError={errorMsg}
                    onSubmitEditing={handleSubmit}
                    isTransparent={true}
                    value={recoveryEmail}
                  />
                </View>
              </FormGroup>
            </FormWrapper>
            {renderButton(
              'forgot-password-submit-button',
              handleSubmit,
              isLoadingRecoveryEmail || !recoveryEmail || errorMsg,
              isLoadingRecoveryEmail ? 'LOADING...' : 'SUBMIT',
            )}
          </>
        ) : (
          <>
            <SociollaText style={styles.descForgetPassword}>
              Reset link sent successfully. Please check your email.
            </SociollaText>
            <Button
              testID="forgot-password-ok-button"
              onPress={() => successForgetPass()}
              title="Ok"
            />
          </>
        )}
      </View>
    </ModalFull>
  );
};

const LoginScreen = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState({});
  const [isForgetPasswordModal, setIsForgetPasswordModal] = useState(false);
  const [payload, setPayload] = useState({
    email: '',
    password: '',
  });

  const setPayloadHandler = (data) => {
    setPayload({
      ...payload,
      ...data,
    });
  };

  const verifiedErrorForm = (payload) => {
    setHasError(
      global.validate(payload, {
        email: {
          presence: { allowEmpty: false },
        },
        password: {
          presence: { allowEmpty: false },
        },
      }),
    );
  };

  const resetError = () => {
    setHasError({});
  };

  const loginHandler = async () => {
    const trimmedEmail = payload.email.trim();
    const trimmedPayload = { ...payload, email: trimmedEmail };
    verifiedErrorForm(trimmedPayload);
    if (hasError && Object.keys(hasError).length) {
      return;
    }
    if (!trimmedEmail || !payload.password) {
      return;
    }

    setIsLoading(true);
    logLoginEvent();

    if (isNative && !IS_SIMULATOR) {
      crashlytics().log('User signed in.');
    }

    if (!payload.password.match(PASSWORD_REGEX)) {
      setIsLoading(false);
      return setHasError({
        password:
          'Password must have at least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character (!@#$%^&*.), no spaces, and no character repeated 3+ times in a row',
      });
    }

    try {
      const response = await dispatch(Auth.login(trimmedPayload));
      handleLoginResponse(response);
    } catch (err) {
      setHasError({ email: 'Email not registered' });
    }
    setIsLoading(false);
  };

  const logLoginEvent = () => {
    logEvent({
      eventName: 'login_process',
      data: { method: isNative ? 'apps' : 'web', ...payload },
      dataKey: 'detail',
    });
  };

  const handleLoginResponse = async (response) => {
    if (!response) {
      return setHasError({ email: 'Email not registered' });
    }

    if (response._id) {
      Toast.show({ text1: 'You have successfully logged in', type: 'success' });
      logUserLogin(response);
      await dispatch(Auth.updateAppVersion());
      return true;
    }

    handleLoginError(response);
  };

  const logUserLogin = (response) => {
    logEvent({
      eventName: 'loggedin',
      data: {
        method: isNative ? 'apps' : 'web',
        user_id: response._id,
        email: response.email,
        username: response.username,
        role: response.role?.code || '',
      },
      dataKey: 'detail',
    });

    if (isNative && !IS_SIMULATOR) {
      crashlytics().setUserId(response._id);
      crashlytics().setAttributes({
        role: response.role?.code || '',
        email: response.email,
        username: response.username,
      });
    }
  };

  const handleLoginError = (response) => {
    if (response.errorCode === 'INCORRECT_PASSWORD') {
      setHasError({ password: 'Email or password mismatch' });
    } else if (response.errorCode === 'INVALID_PASSWORD') {
      setHasError({ password: response.message });
    } else {
      setHasError({ email: 'Email not registered' });
    }
  };

  useEffect(() => {
    if (isNative && !IS_SIMULATOR) {
      crashlytics().log('Login mounted.');
      logEventScreen('LoginScreen');
    }
  }, []);

  const renderInput = (
    label,
    value,
    onChangeText,
    hasError,
    onSubmitEditing,
  ) => (
    <FormGroup>
      <View style={styles.marginBottom}>
        <Input
          testID="login-email-input"
          label={label}
          placeholder=""
          onChangeText={(text) => {
            if (hasError && Object.keys(hasError).length) {
              setHasError({});
            }
            onChangeText(text);
          }}
          onFocus={resetError}
          hasError={hasError}
          onSubmitEditing={onSubmitEditing}
          validate={{
            presence: { allowEmpty: false, message: `${label} is required` },
          }}
          isTransparent={true}
          value={value}
        />
      </View>
    </FormGroup>
  );

  const renderPasswordInput = (
    value,
    onChangeText,
    hasError,
    onSubmitEditing,
  ) => (
    <FormGroup>
      <View style={styles.marginBottom}>
        <InputPassword
          testID="login-password-input"
          label="Password"
          placeholder=""
          onChangeText={(text) => {
            if (hasError && Object.keys(hasError).length) {
              setHasError({});
            }
            onChangeText(text);
          }}
          onFocus={resetError}
          hasError={hasError}
          onSubmitEditing={onSubmitEditing}
          validate={{
            presence: { allowEmpty: false, message: 'Password is required.' },
          }}
          value={value}
          isTransparent={true}
        />
      </View>
    </FormGroup>
  );

  const renderForgotPassword = (setIsForgetPasswordModal) => (
    <View>
      <SociollaText
        testID="forgot-password-link"
        type="regular"
        style={styles.centerText}
        onPress={() => setIsForgetPasswordModal(true)}>
        Forgot Password
      </SociollaText>
    </View>
  );

  const renderVersionApp = () => (
    <View style={styles.versionContainer}>{isWeb && <VersionApp />}</View>
  );

  const renderTitleLogin = () => (
    <SociollaText style={styles.loginTitle}>Login</SociollaText>
  );

  const renderFormLogin = () => (
    <FormWrapper>
      {renderInput(
        'Email Address',
        payload.email,
        (email) => setPayloadHandler({ email: email.replace(/\s/g, '') }),
        hasError?.email,
        loginHandler,
      )}
      {renderPasswordInput(
        payload.password,
        (password) => setPayloadHandler({ password }),
        hasError?.password,
        loginHandler,
      )}
    </FormWrapper>
  );

  const renderButtonContent = () =>
    isLoading ? (
      <View style={styles.loginButton}>
        <ActivityIndicator
          testID="login-loading-indicator"
          size="large"
          color={Colors.blue10}
        />
      </View>
    ) : (
      renderButton(
        'login-submit-button',
        loginHandler,
        !!(
          !payload.email ||
          !payload.password ||
          (hasError && Object.keys(hasError).length)
        ),
        'LOGIN',
      )
    );

  const renderContentLogin = () => (
    <View
      testID="login-form-wrapper"
      style={[
        styles.loginWrapper,
        {
          minHeight: isWeb ? 'auto' : screenHeight,
        },
      ]}>
      {renderTitleLogin()}
      {renderFormLogin()}
      {renderButtonContent()}
      {renderForgotPassword(setIsForgetPasswordModal)}
    </View>
  );

  const renderLoginForm = () => (
    <BgWrapper testID="login-screen" minimumScaling={true}>
      {isMobileWeb && <VersionApp />}
      {renderContentLogin()}
      {isWeb && renderVersionApp()}

      <ModalForgotPassword
        setIsForgetPasswordModal={setIsForgetPasswordModal}
        isForgetPasswordModal={isForgetPasswordModal}
      />
    </BgWrapper>
  );

  return renderLoginForm();
};

const styles = StyleSheet.create({
  loginWrapper: {
    width: isWeb ? 400 : Dimensions.get('window').width,
    alignSelf: isWeb ? 'center' : 'stretch',
    paddingVertical: isWeb ? 14 : 40,
    paddingHorizontal: 16,
    backgroundColor: Colors.white,
    borderTopRightRadius: isWeb ? 5 : 20,
    borderTopLeftRadius: isWeb ? 5 : 20,
    borderBottomRightRadius: isWeb ? 5 : 0,
    borderBottomLeftRadius: isWeb ? 5 : 0,
    marginBottom: 0,
  },
  loginTitle: {
    fontSize: 22,
    fontFamily: 'figtree-semiBold',
    letterSpacing: 0.5,
    marginBottom: isWeb ? 20 : 20,
  },
  versionContainer: {
    marginTop: 50,
    marginBottom: 50,
  },
  marginBottom: {
    marginBottom: isWeb ? 2 : 7,
  },
  loginButton: {
    width: '100%',
    paddingHorizontal: 6,
    paddingVertical: 10,
  },
  centerText: {
    marginTop: isWeb ? 2 : 10,
    marginBottom: 5,
    textAlign: 'center',
    color: '#3346AF',
    fontFamily: 'figtree-semiBold',
    fontSize: 11,
    letterSpacing: 0.5,
  },
  descForgetPassword: {
    paddingBottom: 15,
    marginBottom: 15,
    textAlign: 'justify',
    color: '#171D29',
    fontFamily: 'figtree-regular',
    fontSize: 11,
    letterSpacing: 0.5,
  },
  titleText: {
    fontSize: 11,
    letterSpacing: 0.5,
    color: Colors.white,
  },
  itemCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 20,
    marginBottom: 20,
  },
});

export default React.memo(LoginScreen, (prev, next) => prev == next);
